// ============================================================================
// Google Analytics 4 (GA4) — lightweight gtag loader.
//
// The Measurement ID comes from REACT_APP_GA_MEASUREMENT_ID (see .env.example).
// If it is not set, every function becomes a no-op so the app works offline.
// ============================================================================

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

let loaded = false;

/** Do the actual DOM injection. */
function injectGtag() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/**
 * Inject the gtag script once (safe to call from any module).
 *
 * The gtag bundle is ~173 KB. On a throttled mobile connection that competes
 * with the fonts, hero images and JS that actually determine Largest Contentful
 * Paint, so the download is deferred until the page has finished loading. The
 * dataLayer stub is installed immediately so no call is ever lost - page views
 * recorded before the script arrives are replayed by gtag itself.
 */
export function initGA() {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || loaded) return;
  loaded = true;

  // queue-safe stub, available straight away
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  if (document.readyState === 'complete') {
    injectGtag();
  } else {
    window.addEventListener('load', injectGtag, { once: true });
  }
}

/** Track a single page view. Call on every route change. */
export function trackPageView(path) {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;
  if (!window.gtag) initGA();
  if (window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, { page_path: path || window.location.pathname });
  }
}

/** Track a custom event, e.g. trackEvent('generative_lead', { type: 'bhajan' }). */
export function trackEvent(name, params = {}) {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', name, params);
}
